import React, { useEffect, useCallback, useContext, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { useFormik } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@material-ui/core';
import { Grid, PageContainer } from 'layouts';
import { Groupbox } from 'components/Form';
import { AuthContext, ProductsContext, ValuesContext } from 'contexts';
import { getComparator, stableSort } from 'utils/sortable';

import ProductsFilterSection from './ProductsFilterSection';
import ProductsRefiners from './ProdutsRefiners';
import ProductsTable from './ProductsTable';

const useStyles = makeStyles((theme) => ({
  title: {
    textAlign: 'center',
    padding: '16px 0',
  },
  root: {
    flexGrow: 1,
  },
  filtersGrid: {
    paddingTop: '16px',
  },
  selectCategoryMsgGrid: {
    paddingTop: '32px',
    justifyContent: 'center',
    display: 'flex',
  },
  productsTableGrid: {
    paddingLeft: '16px',
  },
  selectCategoryMsgContent: {
    color: 'rgba(0, 0, 0, 0.38)',
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontSize: '16px',
    letterSpacing: '0.15px',
    lineHeight: '21px',
  },
}));

const Products = (props) => {
  const classes = useStyles();
  const { bu = '', category = '' } = props.match.params;
  const query = new URLSearchParams(props.location?.search || '');
  const qualifier = query.get('qualifier') || '';
  const nameKeywords = query.get('nameKeywords') || '';
  const specifiers = JSON.parse(query.get('specifiers') || '{}');
  const pendingDatasheet = query.get('pendingDatasheet') === 'true';

  const { state, actions } = useContext(ProductsContext);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [openRemoveAllDialog, setOpenRemoveAllDialog] = useState(false);

  const { state: authState, actions: authActions } = useContext(AuthContext);
  const { state: valuesState, actions: valuesActions } = useContext(ValuesContext);

  useEffect(() => {
    if (!state.products.length) {
      actions.getProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenRemoveAllDialog = () => {
    setOpenRemoveAllDialog(true);
  };

  const handleCloseRemoveAllDialog = () => {
    setOpenRemoveAllDialog(false);
  };

  const handleRemoveAll = () => {
    actions.removeAllProductsByBU(bu);
    handleCloseRemoveAllDialog();
  };

  useEffect(() => {
    if (!authState.roles.length) {
      authActions.fetchRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!valuesState.values.length) {
      valuesActions.fetchAllValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!state.productsFilters) {
      actions.getFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const matchSpecifications = (product, specifiers) => {
    return Object.keys(specifiers)
      .filter((k) => !!specifiers[k])
      .every((k) => {
        if (Array.isArray(product.specifications?.[k])) {
          const productSpecifications = product.specifications?.[k].map((e) => String(e));
          if (Array.isArray(specifiers[k])) {
            if (!specifiers[k].length) return true;

            return specifiers[k].some((s) => productSpecifications.includes(s));
          }
          return productSpecifications.includes(specifiers[k]);
        } else if (Array.isArray(specifiers[k])) {
          return specifiers[k].includes(String(product.specifications?.[k]));
        }

        return String(product.specifications?.[k]) === specifiers[k];
      });
  };

  // const matchQualifier = (product, qualifier) => {
  //   if (product.qualifier === undefined) return true;
  //   if (typeof product.qualifier === 'string') return product.qualifier?.includes(qualifier);

  //   return String(product.qualifier) === qualifier;
  // };

  const matchQualifier = (product, qualifier) => {
    if (!product.qualifier) return true;
    if (!qualifier || qualifier.length === 0) return true;
    if (!Array.isArray(product.qualifier)) return qualifier.includes(product.qualifier);

    return product.qualifier.some((q) => qualifier.includes(q));
  };

  const matchNameKeywords = (product, nameKeywords) => {
    if (typeof nameKeywords !== 'string') return false;

    if (!product || typeof product.name !== 'string' || !Array.isArray(product.keywords)) {
      console.warn('Invalid product structure', { product });
      return false;
    }

    const lowerCaseNameKeywords = nameKeywords.toLowerCase().trim();
    return (
      product.sku?.toLowerCase().includes(lowerCaseNameKeywords) ||
      product.name?.toLowerCase().includes(lowerCaseNameKeywords) ||
      product.keywords?.some(
        (k) => typeof k === 'string' && k?.toLowerCase().includes(lowerCaseNameKeywords),
      )
    );
  };

  const matchPendingDatasheet = (product, pendingDatasheet) =>
    (pendingDatasheet && product?.template?.status === 'pending') || !pendingDatasheet;

  const matchFilters = useCallback((values, product) => {
    const { businessUnit, category, nameKeywords, qualifier, pendingDatasheet, specifiers } =
      values;
    return (
      product.business_unit?.includes(businessUnit) &&
      product.category?.includes(category) &&
      matchQualifier(product, qualifier) &&
      matchSpecifications(product, specifiers) &&
      matchNameKeywords(product, nameKeywords) &&
      matchPendingDatasheet(product, pendingDatasheet)
    );
  }, []);

  useEffect(() => {
    const filterValues = {
      businessUnit: bu,
      category,
      nameKeywords,
      qualifier,
      pendingDatasheet,
      specifiers,
    };
    const products = stableSort(state.products, getComparator('asc', 'name'));
    setFilteredProducts(products.filter(matchFilters.bind(null, filterValues)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.products.length]);

  const handleHistoryPath = (values) => {
    let { businessUnit, category, qualifier, nameKeywords, pendingDatasheet, specifiers } = values;
    let nextPath = '/products';
    const query = new URLSearchParams();
    specifiers = Object.keys(specifiers).reduce((r, k) => {
      if (specifiers[k]) r[k] = specifiers[k];
      return r;
    }, {});

    if (businessUnit) nextPath += `/${businessUnit}`;
    if (category) nextPath += `/${category}`;
    //if (qualifier) query.append('qualifier', qualifier);
    if (qualifier.length) query.append('qualifier', JSON.stringify(qualifier));
    if (nameKeywords) query.append('nameKeywords', nameKeywords);
    if (Object.keys(specifiers).length) query.append('specifiers', JSON.stringify(specifiers));
    if (pendingDatasheet) query.append('pendingDatasheet', 'true');

    nextPath += `?${query.toString()}`;
    props.history.push(nextPath);
  };

  const onSubmitFilters = useCallback(
    (values) => {
      handleHistoryPath(values);
      const products = stableSort(state.products, getComparator('asc', 'name'));
      setFilteredProducts(products.filter(matchFilters.bind(null, values)));
    },
    [state.products, matchFilters],
  );

  const formik = useFormik({
    initialValues: {
      businessUnit: bu,
      category,
      nameKeywords,
      qualifier,
      pendingDatasheet,
      specifiers,
    },
    onSubmit: onSubmitFilters,
  });

  useEffect(() => {
    const filterValues = {
      ...formik.values,
      businessUnit: bu,
      category,
      nameKeywords,
      qualifier,
      pendingDatasheet,
      specifiers,
    };
    formik.setValues(filterValues);
    const products = stableSort(state.products, getComparator('asc', 'name'));
    setFilteredProducts(products.filter(matchFilters.bind(null, filterValues)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bu, category, props.location?.search]);

  const canEdit = authState?.roles?.includes('admin') || authState?.roles?.includes(`${bu}_edit`);
  const canApproveDatasheet =
    authState?.roles?.includes('admin') || authState?.roles?.includes(`${bu}_approve_datasheet`);

  return (
    <PageContainer component="main" maxWidth="md">
      <formik onSubmit={formik.handleSubmit}>
        <Grid container>
          <Grid item xs={12}>
            <Groupbox>
              <ProductsFilterSection
                productsFilters={state.productsFilters}
                canApproveDatasheet={canApproveDatasheet}
                roles={authState.roles}
                onRemoveAll={handleOpenRemoveAllDialog}
                {...formik}
              />
            </Groupbox>
          </Grid>
          {formik.values.businessUnit === '' ||
          formik.values.category === '' ||
          !state.productsFilters ? (
            <Grid className={classes.selectCategoryMsgGrid} item xs={12}>
              <span className={classes.selectCategoryMsgContent}>
                Please select a Business Unit and Category to see Products and Refiners.
              </span>
            </Grid>
          ) : (
            <>
              <Grid className={classes.filtersGrid} item xs={4}>
                <ProductsRefiners
                  {...formik}
                  refiners={
                    state.productsFilters.filters[formik.values.businessUnit][
                      formik.values.category
                    ]
                  }
                  refinersValues={valuesState.values.filter(
                    (v) =>
                      v.business_unit === formik.values.businessUnit &&
                      v.category === formik.values.category,
                  )}
                  schema={state.productsFilters.schemes[formik.values.businessUnit].find(
                    (e) => e.route === formik.values.category,
                  )}
                  products={filteredProducts}
                />
              </Grid>
              <Grid className={classes.productsTableGrid} item xs={8}>
                <ProductsTable canEdit={canEdit} products={filteredProducts} business_unit={formik.values.businessUnit} />
              </Grid>
            </>
          )}
        </Grid>
      </formik>
      <Dialog
        open={openRemoveAllDialog}
        onClose={handleCloseRemoveAllDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Remove all products?'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to remove all products for the selected Business Unit? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleCloseRemoveAllDialog} color="primary">
            Cancel
          </MuiButton>
          <MuiButton onClick={handleRemoveAll} color="primary" autoFocus>
            Remove All
          </MuiButton>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default withRouter(Products);
